const { RichText, URLInput, ColorPalette } = wp.blockEditor;
const {
  Button,
  Icon,
  ToggleControl,
  Popover,
  TextControl,
  Tooltip,
  SelectControl,
} = wp.components;
const { Fragment, useState, useRef } = wp.element;
const { __ } = wp.i18n;

const customColors = [
  { name: 'White', color: '#ffffff' },
  { name: 'Dark Green Link', color: '#57c79b' },
  { name: 'Dark Blue Link', color: '#b8d1e0' },
  { name: 'Light Green Link', color: '#00b870' },
  { name: 'Light Blue Link', color: '#0075b8' },
  { name: 'Light Black', color: '#1c1c1c' },
  { name: 'Light Turquoise', color: '#e7f4ef' },
  { name: 'Dark Grey', color: '#525252' },
  { name: 'Dark Green', color: '#00342e' },
  { name: 'Green', color: '#00764f' },
  { name: 'Expressive Green', color: '#94d1b9' },
  { name: 'Forest Green', color: '#005237' },
  { name: 'Bright Green', color: '#00b870' },
  { name: 'Pale Aqua Green', color: '#c9e8dc' },
  { name: 'Lighten Blue', color: '#e6eff4' },
  { name: 'Dark Blue', color: '#002033' },
  { name: 'Blue', color: '#004080' },
  { name: 'Expressive Blue', color: '#94c5e1' },
  { name: 'Forest Blue', color: '#003366' },
  { name: 'Bright Blue', color: '#007bff' },
  { name: 'Pale Aqua Blue', color: '#c9e0f2' },
];

/**
 * PgButton Component
 *
 * @param {Object} props
 * @param {string} props.link - The URL of the button.
 * @param {string} props.label - The text of the button.
 * @param {string} props.buttonClass - The CSS class of the button.
 * @param {Function} props.onUpdate - Callback for updating button properties.
 * @param {Function} props.onRemove - Callback for removing the button.
 */
const PgButton = ({
  link,
  label,
  buttonClass = 'btn btn-dark btn-solid',
  additionalClass = '',
  onUpdate,
  onRemove,
  style = {},
  linkField = 'link',
  labelField = 'label',
  buttonClassField = 'buttonClass',
  buttonTextColorField = null,
  buttonTextColor = '',
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [localLink, setLocalLink] = useState(link || '');
  const [isFocused, setIsFocused] = useState(false);

  const popoverRef = useRef();

  // Predefined colors and their corresponding classes
  const colorOptions = [
    { name: 'Dark', className: 'btn-dark', color: '#00764f' },
    { name: 'Light', className: 'btn-light', color: '#FCFCFC' },
    { name: 'Pine', className: 'btn-pine', color: '#A8E868' },
    { name: 'Aurora', className: 'btn-aurora', color: '#95DACC' },
    { name: 'Crocus', className: 'btn-crocus', color: '#F3AAD1' },
    { name: 'Sun', className: 'btn-sun', color: '#FFB47A' },
  ];

  const viewModeOptions = [
    { label: 'Desktop & Mobile', value: null },
    { label: 'Desktop Only', value: 'only-dektop-flex' },
    { label: 'Mobile Only', value: 'only-mobile-flex' },
  ];

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const handleUrlChange = (newLink) => {
    setLocalLink(newLink); // Update local state
  };

  const commitUrlChange = () => {
    onUpdate({ [linkField]: localLink }); // Commit changes to the block attribute
  };

  const getCurrentColorClass = () => {
    const colors = colorOptions.filter((color) =>
      buttonClass.includes(color.className)
    );

    return colors.length > 0 ? colors[0].className : 'bg-dark';
  };

  const getCurrentVisibilityMode = () => {
    const viewModes = viewModeOptions.filter((viewMode) =>
      buttonClass.includes(viewMode.value)
    );

    return viewModes.length > 0 ? viewModes[0].value : null;
  };

  const handleToggleSolidClass = (isSolid) => {
    const currentColorClass = getCurrentColorClass();
    const currentVisibilityMode = getCurrentVisibilityMode();
    const classArray = [
      additionalClass,
      'btn',
      currentColorClass,
      isSolid ? 'btn-solid' : 'btn-outline',
      currentVisibilityMode,
    ];

    const classes = [...new Set(classArray.filter())].join(' ');
    onUpdate({
      [buttonClassField]: classes,
    });
  };

  const handleToggleTextClass = (isText) => {
    const currentColorClass = getCurrentColorClass();
    const currentVisibilityMode = getCurrentVisibilityMode();
    const classArray = [
      additionalClass,
      'btn',
      currentColorClass,
      isText ? 'btn-text' : 'btn-outline',
      currentVisibilityMode,
    ];

    const classes = [...new Set(classArray.filter())].join(' ');
    onUpdate({
      [buttonClassField]: classes,
    });
  };

  const handleColorChange = (colorClass) => {
    const currentVisibilityMode = getCurrentVisibilityMode();
    const currentStyleClasses = ['btn-text', 'btn-outline', 'btn-solid']
      .filter((style) => buttonClass.includes(style))
      .join(' ');

    const classArray = currentStyleClasses.split(' ').filter(Boolean);
    classArray.push(colorClass);
    classArray.unshift('btn');
    const classes = [...new Set(classArray.filter(Boolean))].join(' ');

    onUpdate({
      [buttonClassField]: classes,
    });
  };

  const handleVisibilityMode = (newVisibilityMode) => {
    viewModeOptions.forEach((viewMode) => {
      buttonClass = buttonClass.replace(viewMode.value, '');
    });
    const classArray = buttonClass.split(' ').filter(Boolean);
    classArray.push(newVisibilityMode);
    classArray.unshift('btn');
    const classes = [...new Set(classArray.filter(Boolean))].join(' ');

    onUpdate({
      [buttonClassField]: classes,
    });
  };

  const handleTextColorChange = (textColor) => {
    onUpdate({
      [buttonTextColorField]: textColor,
    });
  };

  return (
    <Fragment>
      <div className={`pg-button ${additionalClass}`}>
        <a href={`#${link ?? ''}`} className={buttonClass} style={style}>
          <RichText
            tagName="span"
            value={label}
            onChange={(newLabel) => onUpdate({ [labelField]: newLabel })}
            keepPlaceholderOnFocus={true}
            placeholder={__('Button Label')}
          />
        </a>

        {/* Button Actions */}
        <div className="pg-button-actions">
          {/* Settings Button */}
          <Button
            isSmall
            onClick={toggleSettings}
            className="pg-button-settings"
            ref={popoverRef}
          >
            <Icon icon="admin-generic" />
          </Button>

          {onRemove && (
            <Button isSmall isDestructive onClick={onRemove}>
              <Icon icon="no-alt" />
            </Button>
          )}
        </div>

        {/* Settings Popover */}
        {showSettings && (
          <Popover
            anchor={popoverRef.current}
            onClose={() => setShowSettings(false)}
            position="bottom center"
            focusOnMount={false}
          >
            <div className="pg-button-settings-popover">
              <h4>{__('Button Settings', 'pg')}</h4>

              {/* URL Input */}
              <div className="pg-button-url-input">
                <label>{__('Button URL', 'pg')}</label>
                <URLInput
                  value={localLink}
                  onChange={handleUrlChange}
                  autoFocus={false}
                  onFocus={() => setIsFocused(true)}
                  onBlur={commitUrlChange} // Commit on blur
                  placeholder={__('Enter button URL', 'pg')}
                />
              </div>

              {/* Button Style Toggle */}
              <ToggleControl
                label={__('Solid?', 'pg')}
                checked={buttonClass.includes('btn-solid')}
                onChange={handleToggleSolidClass}
              />

              {/* Button Text Style Toggle */}
              <ToggleControl
                label={__('Is text button?', 'pg')}
                checked={buttonClass.includes('btn-text')}
                onChange={handleToggleTextClass}
              />

              {/* Color Selector */}
              <div className="pg-button-color-picker">
                <h5>{__('Button Color', 'pg')}</h5>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  {colorOptions.map((color) => (
                    <Tooltip key={color.className} text={color.name}>
                      <Button
                        className={color.className}
                        style={{
                          backgroundColor: color.color,
                          width: buttonClass.includes(color.className)
                            ? '2rem'
                            : '1.5rem',
                          height: buttonClass.includes(color.className)
                            ? '2rem'
                            : '1.5rem',
                          border: buttonClass.includes(color.className)
                            ? '2px solid green'
                            : '2px solid black',
                          borderRadius: '50%',
                        }}
                        onClick={() => handleColorChange(color.className)}
                      />
                    </Tooltip>
                  ))}
                </div>
              </div>

              {buttonTextColorField && (
                <>
                  <h5>{__('Button Text Color', 'pg')}</h5>
                  <ColorPalette
                    value={buttonTextColor}
                    onChange={(color) => handleTextColorChange(color)}
                    colors={customColors}
                    disableCustomColors={true}
                  >
                    <button
                      onClick={() => setColor(undefined)}
                      className="components-button is-link"
                      style={{ marginTop: '10px' }}
                    >
                      {__('Reset Color', 'pg')}
                    </button>
                  </ColorPalette>
                </>
              )}

              <SelectControl
                label={__('View mode')}
                value={getCurrentVisibilityMode()}
                options={viewModeOptions}
                onChange={handleVisibilityMode}
              />

              {/* Close Settings */}
              <Button
                isPrimary
                onClick={() => {
                  commitUrlChange();
                  setShowSettings(false);
                }}
                className="pg-button-close-settings"
              >
                {__('Close Settings', 'pg')}
              </Button>
            </div>
          </Popover>
        )}
      </div>
    </Fragment>
  );
};

export default PgButton;
